import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { PdfPagesScrollDirective } from '../../source/pdf-pages-virtual-scroll';
import { PdfPageCanvasDirective, PdfPageRenderedEvent } from '../../source/pdf-page-canvas.directive';

/**
 * Lazy-loading PDF page viewer: a CDK virtual-scroll viewport driven by
 * PdfPagesVirtualScrollStrategy (offset table from known page sizes) where each
 * page renders independently to its own canvas via PdfPageCanvasDirective.
 * Purely presentational — layout math, zoom anchoring and bounding-box overlays
 * stay with the parent, which reaches the viewport through the exposed handles.
 */
@Component({
  selector: 'app-pdf-lazy-viewer',
  standalone: true,
  imports: [CommonModule, ScrollingModule, PdfPagesScrollDirective, PdfPageCanvasDirective],
  templateUrl: './pdf-lazy-viewer.component.html',
  styleUrl: './pdf-lazy-viewer.component.scss',
})
export class PdfLazyViewerComponent {
  @Input() pdfDoc: any = null;
  @Input() pageNumbers: number[] = [];
  @Input() pageWidthsPx: number[] = [];
  @Input() pageHeightsPx: number[] = [];
  @Input() pageScales: number[] = [];
  @Input() pageRotations = new Map<number, number>();
  @Input() pageGapPx = 16;
  @Input() pxPerRem = 16;
  /** Viewports of rendered headroom above/below the visible area (default 1.5,
   *  matching the source page). Smaller values fetch/render fewer pages ahead. */
  @Input() bufferViewports = 1.5;

  @Output() scrolledIndexChange = new EventEmitter<number>();
  @Output() pageRendered = new EventEmitter<PdfPageRenderedEvent>();

  @ViewChild(CdkVirtualScrollViewport) viewport?: CdkVirtualScrollViewport;
  @ViewChild(CdkVirtualScrollViewport, { read: ElementRef }) viewportElementRef?: ElementRef<HTMLElement>;
  @ViewChild(PdfPagesScrollDirective) scrollDirective?: PdfPagesScrollDirective;

  get scrollerElement(): HTMLElement | undefined {
    return this.viewportElementRef?.nativeElement;
  }

  readonly trackByPageNumber = (_index: number, page: number): number => page;

  /** Page wrappers are sized in rem so geometry tracks the root font size. */
  remOf(px: number | undefined): string {
    return `${((px ?? 0) / this.pxPerRem).toFixed(4)}rem`;
  }
}
